import { Box, Typography } from '@mui/material';
import { memo } from 'react';
import {
	SafetyCourseStatus,
	safetyCourseStatus,
	safetyCourseTypeNames,
	SafetyCourseValidationResponse,
} from '../../../../constants/SafetyCourse';
import { formatSlashedDate } from '../../../helpers/dateFormatters';
import { SafetyCourseStatusIcon } from '../SafetyCourseStatusIcon/SafetyCourseStatusIcon';

type SafetyCourseValidationInfoProps = {
	status: SafetyCourseStatus;
	safetyCourse: SafetyCourseValidationResponse | null;
};

const SafetyCourseValidationInfoImpl = ({
	status,
	safetyCourse,
}: SafetyCourseValidationInfoProps): JSX.Element => {
	if (!safetyCourse || status === safetyCourseStatus.NotFound) {
		return (
			<>
				<SafetyCourseStatusIcon status={status} />
				<Box pl={2}>
					<Typography variant="body1Bold">
						No Safety Course Found
					</Typography>
					<Typography variant="subtitle2" color="text.secondary">
						{`Confirm that the employees' safety course ID is correct.`}
					</Typography>
					<Typography variant="subtitle2" color="text.secondary">
						Check that their account is not private and is in the
						safety course system.
					</Typography>
				</Box>
			</>
		);
	}
	switch (status) {
		case safetyCourseStatus.Expired:
			return (
				<>
					<SafetyCourseStatusIcon status={status} />
					<Box pl={2}>
						<Typography variant="body1Bold">
							Safety Course Has Expired
						</Typography>
						<Typography variant="subtitle2" color="text.secondary">
							{safetyCourse.id} - {safetyCourse.name}
						</Typography>
						<Typography variant="subtitle2" color="text.secondary">
							Expired on{' '}
							{safetyCourse.expiryDate
								? formatSlashedDate(safetyCourse.expiryDate)
								: 'unknown date'}
						</Typography>
					</Box>
				</>
			);
		case safetyCourseStatus.Valid:
			return (
				<>
					<SafetyCourseStatusIcon status={status} />
					<Box pl={2}>
						<Typography variant="body1Bold">
							Current Safety Course Found
						</Typography>
						<Typography variant="subtitle2" color="text.secondary">
							{safetyCourse.id} - {safetyCourse.name}
						</Typography>
						{safetyCourse.expiryDate && (
							<Typography
								variant="subtitle2"
								color="text.secondary">
								Valid until{' '}
								{formatSlashedDate(safetyCourse.expiryDate)}
							</Typography>
						)}
					</Box>
				</>
			);
		case safetyCourseStatus.Unverified:
			return (
				<>
					<SafetyCourseStatusIcon status={status} />
					<Box pl={2}>
						<Typography variant="body1Bold">
							Unable to Verify Safety Course
						</Typography>
						<Typography variant="subtitle2" color="text.secondary">
							{`Unable to reach ${
								safetyCourseTypeNames[safetyCourse.type]
							} to verify the account.`}
						</Typography>
					</Box>
				</>
			);
	}
};

export const SafetyCourseValidationInfo = memo(SafetyCourseValidationInfoImpl);
